import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentAccountSelectionV2Data,
  type EnrollmentAccountSelectionV2SubmitInput,
  type EnrollmentAccountSelectionV2SubmitOutput,
} from '../../../../../models'
import { createMutationHook, createQueryHook } from '../../../../../hooks'

/**
 * Get Enrollment AccountSelectionV2 data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentAccountSelectionV2Data>
 * @example const response = await getEnrollmentAccountSelectionV2Data()
 */
export const getEnrollmentAccountSelectionV2Data = async (
  context?: DefaultContext,
): Promise<EnrollmentAccountSelectionV2Data> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentAccountSelectionV2Data',
      query: `mutation getEnrollmentAccountSelectionV2Data($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
              userInfo {
                investment {
                  canEnrollBrokerage
                  canEnrollTraditionalIra
                  canEnrollRothIra 
                  canEnrollInheritedRothIra
                  canEnrollInheritedTraditionalIra
                }
              }
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentAccountSelectionV2Data }
  return response.submitEnrollment
}

export const useGetEnrollmentAccountSelectionV2Data = createQueryHook<undefined, EnrollmentAccountSelectionV2Data>(
  getEnrollmentAccountSelectionV2Data,
)

/**
 * Submit Enrollment AccountSelectionV2 data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentAccountSelectionV2SubmitOutput>
 * @example const response = await submitEnrollmentAccountSelectionV2Data();
 */
export const submitEnrollmentAccountSelectionV2Data = async (
  enrollmentAccountSelectionV2SubmitInput: EnrollmentAccountSelectionV2SubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentAccountSelectionV2SubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentAccountSelectionV2Data',
      query: `mutation submitEnrollmentAccountSelectionV2Data($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentAccountSelectionV2SubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentAccountSelectionV2SubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentAccountSelectionV2Data = createMutationHook<
  EnrollmentAccountSelectionV2SubmitInput,
  EnrollmentAccountSelectionV2SubmitOutput
>(submitEnrollmentAccountSelectionV2Data)
